import {
  StatusDeviceEgress,
  StatusEgressState,
  type ControlDevice,
  type StatusDevice,
  type StatusDeviceEgressRTMPDestination
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import { useGetMachineAgentId } from '#~/agent/components/bridge/hooks.js';
import { useAgents } from '#~/agent/hooks.js';
// Gating (spec §6): a device gated by transitory/INVALID auth must not be an
// NDI/RTMP output target. Per `reference-rules-in-comments` (global).
import { applyVideoGating, useGatedVideoIds } from '#~/room/use-gating.js';
import { fieldKeyedAgentsEqual, idNameStatusPick } from '#~/agent/provider/use-agents-field-eq.js';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';
import { DeviceEgressSelector } from '#~/statequery/selectors/device-egress.js';
import type { StudioDevice } from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useCallback, useMemo } from 'react';
import { setNDIDeviceStatus } from '../commands/command-ndi.js';
import { setDeviceStatus } from '../commands/index.js';
import { StreamOperation, type Destination } from '../rtmp/store/destinations.js';
import type { ProfileConfig } from '../rtmp/utils.js';
import type { Device } from '@studio/models/device';

export interface CompositeDevice {
  id: string;
  control: ControlDevice;
  status: T_Maybe<StatusDevice>;
  studio: T_Maybe<StudioDevice>;
}

/**
 * Checks if a device is outputting.
 *
 * @param status - The status of the device.
 * @returns True if the device is outputting, false otherwise.
 */
export const isOutputting = (status: T_Maybe<StatusDeviceEgress>) =>
  status?.ndiOutputStatus?.state === StatusEgressState.EGRESS_STATE_ACTIVE;

export const isRTMPOutputting = (status: T_Maybe<StatusDeviceEgress>) => {
  const rtmpStatus = status?.rtmpStatus;
  if (!rtmpStatus) return false;
  if (rtmpStatus.state === StatusEgressState.EGRESS_STATE_ACTIVE) {
    return true;
  }
  const destinations = rtmpStatus.destinations ?? {};
  return Object.values(destinations).some(
    (destination) => destination?.state === StatusEgressState.EGRESS_STATE_ACTIVE
  );
};

const buildRTMPDestination = (
  destinationId: string,
  destinationStatus: StatusDeviceEgressRTMPDestination
): Destination => ({
  id: destinationId,
  nickName: destinationStatus.nickName ?? '',
  url: destinationStatus.url ?? '',
  streamKey: destinationStatus.streamKey ?? '',
  requireAuth: false,
  credentials: {}
});

const getDefaultProfileConfig = (deviceId: Device['id']): ProfileConfig => ({
  deviceId: deviceId as any,
  width: 1920,
  height: 1080,
  depth: 24,
  frameRate: 30,
  audioCodec: 'Opus' as ProfileConfig['audioCodec'],
  audioBitrate: 128,
  audioFrequency: 44100,
  videoCodec: 'H264 Main' as ProfileConfig['videoCodec'],
  videoBitrate: 6000,
  keyFrameInterval: 4
});

// The default 1000ms sampling skips every dispatch tick inside a subscriber's
// window and never refreshes at its end, so a state that lives under a second
// on the client can go unseen. Observers that act on transitions read every
// tick instead. Module scope keeps the selector identity stable, which keeps
// the state-query subscription from being rebuilt on each render.
const DeviceEgressEveryTickSelector = { ...DeviceEgressSelector, minInterval: 0 };

/**
 * Custom hook to manage outputting devices.
 *
 * This hook provides a list of devices and a function to stop outputting
 * for all currently active devices. It also handles the case where the
 * machine agent is offline, in which case the list of devices will be empty.
 *
 * @param options.everyTick - Refresh on every State Sync dispatch instead of at
 *   most once per second. Only for consumers that must observe each egress
 *   state, such as a recording that is ACTIVE for less than a second.
 * @returns An object containing:
 *  - `stopOutput`: A callback function to stop NDI output for all currently outputting devices.
 *  - `devices`: An object where keys are device IDs and values are device data,
 *    including their egress status. This list is cleared if the machine agent is offline.
 */
export const useOutputting = ({ everyTick = false }: { everyTick?: boolean } = {}) => {
  const allDevices = useMultiDeviceStateQuery(
    everyTick ? DeviceEgressEveryTickSelector : DeviceEgressSelector
  );
  const { agentId: machineAgentId } = useGetMachineAgentId();
  const agents = useAgents(
    (a) => a.map((a) => ({ id: a.id, name: a.name, status: a.status })),
    (left, right) => fieldKeyedAgentsEqual(left, right, idNameStatusPick)
  );
  // Gating §6 — output (NDI + RTMP) capability. A committed device whose
  // stream auth is INVALID stays a non-target: drop it from the source map
  // so it never appears as a routable/streamable output. (Placeholders are
  // never in the state-sync map to begin with — see use-recording for the
  // same filter applied to recording/routing.)
  const gatedIds = useGatedVideoIds();

  // Depend on STABLE inputs. `agents` comes from `useAgents` with no equality
  // fn, so it is a fresh array on every depot tick; listing it here made this
  // memo recompute constantly. That was harmless while the body returned
  // `allDevices` itself, but the §6 gating filter started returning a NEW object
  // per recompute, which React Query reads as changed options -> observer
  // re-subscribe -> re-render -> recompute. Measured 2.2 -> 0.19 cores.
  // `gatedIds` is already stable (useGatedVideoIds memoises over a `shallow`
  // zustand selector), so depending on it directly is safe — the loop came from
  // `agents`, and `applyVideoGating` preserves identity when nothing is gated.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const isMachineAgentOffline =
    agents.find((agent) => agent.id === machineAgentId)?.status === 'offline';
  const devices = useMemo(() => {
    if (isMachineAgentOffline) {
      return {};
    }

    return applyVideoGating(allDevices, gatedIds) as typeof allDevices;
  }, [allDevices, isMachineAgentOffline, gatedIds]);

  const outputtingDeviceIds = useMemo(() => {
    return Object.values(
      Object.entries(devices).filter(([_, device]) => isOutputting(device.egress))
    ).map(([id]) => id);
  }, [devices]);

  const stopOutput = useCallback(() => {
    Promise.all(
      outputtingDeviceIds.map((deviceId) => {
        setNDIDeviceStatus(deviceId as any, { enable: false });
      })
    );
  }, [outputtingDeviceIds]);

  const stopRTMPOutput = useCallback(() => {
    const stopRequests: Promise<void>[] = [];

    for (const device of Object.values(devices)) {
      const rtmpStatus = device.egress?.rtmpStatus;
      if (!rtmpStatus) continue;

      const destinationEntries = Object.entries(rtmpStatus.destinations ?? {});
      for (const [destinationId, destinationStatus] of destinationEntries) {
        if (!destinationStatus) continue;
        const destinationState = destinationStatus.state ?? rtmpStatus.state;
        if (destinationState !== StatusEgressState.EGRESS_STATE_ACTIVE) continue;

        const destination = buildRTMPDestination(destinationId, destinationStatus);
        const profileConfig = getDefaultProfileConfig(device.id);

        stopRequests.push(
          setDeviceStatus(device.id, false, {
            rtmp: {
              enable: false,
              destination,
              profileConfig,
              operation: StreamOperation.Stop
            }
          })
        );
      }
    }

    Promise.all(stopRequests);
  }, [devices]);

  const stopAllOutput = useCallback(() => {
    stopOutput();
    stopRTMPOutput();
  }, [stopOutput, stopRTMPOutput]);

  return {
    stopOutput,
    stopAllOutput,
    devices
  };
};

// size in bytes. Format in KB/MB/GB based on nearest unit with 2 decimal units.
const toKB = (size: number) => (size / 1024).toFixed(2);
const toMB = (size: number) => (size / (1024 * 1024)).toFixed(2);
const toGB = (size: number) => (size / (1024 * 1024 * 1024)).toFixed(2);

/**
 * Formats a size in bytes to a human-readable format.
 *
 * @param size - The size in bytes.
 * @returns A string representing the size in KB/MB/GB based on the nearest unit with 2 decimal units.
 */
export const displaySizeFromBytes = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${toKB(size)} KB`;
  if (size < 1024 * 1024 * 1024) return `${toMB(size)} MB`;
  return `${toGB(size)} GB`;
};

/**
 * Formats a bitrate in bytes to a human-readable format.
 *
 * @param bitrate - The bitrate in bytes.
 * @returns A string representing the bitrate in kbits/s.
 */
export const displayBitrateFromBytes = (bitrate: number) => {
  const bitrateInBits = bitrate * 8;

  const toKbps = (bitrateInBits / 1024).toFixed(2);

  return `${toKbps} kbits/s`;
};

/**
 * Formats a resolution to a human-readable format.
 *
 * @param width - The width of the resolution.
 * @param height - The height of the resolution.
 * @returns A string representing the resolution.
 */
export const displayResolution = (width: number, height: number) => {
  const resolutionMap: Record<string, string> = {
    '7680x4320': '8K',
    '3840x2160': '4K',
    '2560x1440': '1440p',
    '1920x1080': '1080p',
    '1280x720': '720p',
    '854x480': '480p',
    '640x360': '360p',
    '426x240': '240p'
  };

  const resolution = `${width}x${height}`;

  return resolutionMap[resolution] ?? resolution;
};
